/**
 * Utilities for converting between the UI's ConditionalVisibilityState map
 * and the API's SurveyForm model.
 *
 * - buildSurveyForm: fields + visibilityMap → SurveyForm (for save)
 * - hydrateConditionalVisibilityMap: SurveyForm → visibilityMap (for load)
 */

import {
	FilterOperator,
	FormFieldType,
	IFormField,
	LogicalQuestionVisibility,
	QuestionVisibility,
	SimpleEqualityQuestionVisibility,
	SurveyForm,
	SurveyPart,
	SurveyQuestion,
} from '@ViewModels';
import { v4 as uuid } from 'uuid';
import { isSurveySection } from '../../QuestionsWithSections/types';
import type { SurveyPart as SurveyPartV2 } from '../../QuestionsWithSections/types';
import { ConditionalVisibilityState } from '../types';

/**
 * Maps FormFieldType to the API's SurveyQuestion _type string
 */
const fieldTypeToQuestionType: Partial<Record<FormFieldType, string>> = {
	[FormFieldType.String]: 'ShortAnswerQuestion',
	[FormFieldType.Option]: 'DropDownQuestion',
	[FormFieldType.MultipleOptions]: 'CheckBoxesQuestion',
	[FormFieldType.FileUpload]: 'FileUploadQuestion',
};

/**
 * Builds a QuestionVisibility object from a ConditionalVisibilityState.
 *
 * Returns:
 * - null if no valid answer selections
 * - SimpleEqualityQuestionVisibility for a single answer
 * - LogicalQuestionVisibility (Or) for multiple answers
 */
const buildVisibleWhen = (state: ConditionalVisibilityState): QuestionVisibility | null => {
	if (!state.enabled || !state.dependsOnFieldId) return null;

	const validOptionIds = state.dependsOnOptionIds?.filter(id => id && id.trim() !== '') ?? [];
	if (validOptionIds.length === 0) return null;

	if (validOptionIds.length === 1) {
		return {
			_type: 'SimpleEqualityQuestionVisibility',
			fieldId: state.dependsOnFieldId,
			fieldValue: validOptionIds[0],
		} as SimpleEqualityQuestionVisibility;
	}

	return {
		_type: 'LogicalQuestionVisibility',
		logicalOperator: FilterOperator.Or,
		visibilities: validOptionIds.map(
			optionId =>
				({
					_type: 'SimpleEqualityQuestionVisibility',
					fieldId: state.dependsOnFieldId!,
					fieldValue: optionId,
				}) as SimpleEqualityQuestionVisibility
		),
	} as LogicalQuestionVisibility;
};

/**
 * Builds a SurveyForm from customForm fields and the conditional visibility map.
 *
 * Every field in customForm gets a corresponding SurveyPart entry (in order).
 * The visibleWhen property is populated from the conditionalVisibilityMap.
 */
export const buildSurveyForm = (
	fields: IFormField<string>[],
	conditionalVisibilityMap: Map<string, ConditionalVisibilityState>
): SurveyForm => {
	const surveyParts: SurveyPart[] = fields.map(field => {
		const questionType = fieldTypeToQuestionType[field.fieldType] ?? 'ShortAnswerQuestion';
		const visibilityState = conditionalVisibilityMap.get(field.id);
		const visibleWhen = visibilityState ? buildVisibleWhen(visibilityState) : null;

		return {
			id: field.id,
			_type: questionType,
			fieldId: field.id,
			visibleWhen,
		};
	});

	return { isEnabled: true, surveyParts };
};

/**
 * Hydrates a ConditionalVisibilityState map from a SurveyForm.
 *
 * Parses each SurveyPart's visibleWhen to reconstruct the UI state:
 * - null → no entry (or enabled: false)
 * - SimpleEqualityQuestionVisibility → single answer
 * - LogicalQuestionVisibility (Or) → multiple answers
 */
export const hydrateConditionalVisibilityMap = (surveyForm?: SurveyForm): Map<string, ConditionalVisibilityState> => {
	const map = new Map<string, ConditionalVisibilityState>();
	if (!surveyForm?.surveyParts) return map;

	for (const part of surveyForm.surveyParts) {
		const question = part as { fieldId?: string; visibleWhen?: QuestionVisibility | null };
		if (!question.fieldId || !question.visibleWhen) continue;

		const visibility = question.visibleWhen;

		if (visibility._type === 'SimpleEqualityQuestionVisibility') {
			const simple = visibility as SimpleEqualityQuestionVisibility;
			map.set(question.fieldId, {
				enabled: true,
				dependsOnFieldId: simple.fieldId,
				dependsOnOptionIds: [simple.fieldValue],
			});
		} else if (visibility._type === 'LogicalQuestionVisibility') {
			const logical = visibility as LogicalQuestionVisibility;
			if (logical.logicalOperator === FilterOperator.Or && logical.visibilities?.length > 0) {
				const firstVisibility = logical.visibilities[0] as SimpleEqualityQuestionVisibility;
				const optionIds = logical.visibilities
					.filter(v => v._type === 'SimpleEqualityQuestionVisibility')
					.map(v => (v as SimpleEqualityQuestionVisibility).fieldValue);

				map.set(question.fieldId, {
					enabled: true,
					dependsOnFieldId: firstVisibility.fieldId,
					dependsOnOptionIds: optionIds,
				});
			}
		}
	}

	return map;
};

/**
 * Remaps field IDs and option IDs in a SurveyForm for clone operations.
 *
 * Used when cloning a survey — all field and option IDs get new UUIDs,
 * so references inside surveyForm must be updated to match.
 */
const remapVisibility = (
	visibility: QuestionVisibility,
	fieldIdMap: Map<string, string>,
	optionIdMap: Map<string, string>
): QuestionVisibility => {
	if (visibility._type === 'SimpleEqualityQuestionVisibility') {
		const simple = visibility as SimpleEqualityQuestionVisibility;
		return {
			_type: simple._type,
			fieldId: fieldIdMap.get(simple.fieldId) || simple.fieldId,
			fieldValue: optionIdMap.get(simple.fieldValue) || simple.fieldValue,
		} as SimpleEqualityQuestionVisibility;
	}
	if (visibility._type === 'LogicalQuestionVisibility') {
		const logical = visibility as LogicalQuestionVisibility;
		return {
			_type: logical._type,
			logicalOperator: logical.logicalOperator,
			visibilities: logical.visibilities.map(v => remapVisibility(v, fieldIdMap, optionIdMap)),
		} as LogicalQuestionVisibility;
	}
	return visibility;
};

const remapQuestion = (question: SurveyQuestion, fieldIdMap: Map<string, string>, optionIdMap: Map<string, string>) => {
	const newFieldId = fieldIdMap.get(question.fieldId) || question.fieldId;
	return {
		...question,
		id: newFieldId,
		fieldId: newFieldId,
		visibleWhen: question.visibleWhen ? remapVisibility(question.visibleWhen, fieldIdMap, optionIdMap) : null,
	};
};

export const remapSurveyFormIds = (
	surveyForm: SurveyForm,
	fieldIdMap: Map<string, string>,
	optionIdMap: Map<string, string>
): SurveyForm => {
	return {
		isEnabled: true,
		surveyParts: surveyForm.surveyParts.map(part => {
			// V2 SurveySection parts carry their questions in a nested `questions` array
			// and have no `fieldId` of their own. The flat-question branch below would set
			// the section's `id`/`fieldId` to undefined (failing the yup `id.required()`
			// gate → red header) and leave nested question fieldIds unremapped (so the
			// clone's later denormalize join against the remapped fields misses, dropping
			// each question's label/type/options). Remap the section id and every nested
			// question instead.
			if (isSurveySection(part as SurveyPartV2)) {
				// Operate on the @ViewModels shape (not the FE `SurveySection` type) so the
				// nested questions stay compatible with `remapQuestion`. Drop the section-level
				// `visibleWhen` (always external on a clone) by omitting it — matches
				// `duplicateSection`.
				const section = part as SurveyPart & { questions?: SurveyQuestion[]; visibleWhen?: QuestionVisibility | null };
				const { visibleWhen: _sectionVisibility, ...sectionRest } = section;
				return {
					...sectionRest,
					id: uuid(),
					questions: (section.questions ?? []).map(q => remapQuestion(q, fieldIdMap, optionIdMap)),
				};
			}
			return remapQuestion(part as SurveyQuestion, fieldIdMap, optionIdMap);
		}),
	};
};
